import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { 
  Mail, Bell, Send, RefreshCw, AlertTriangle, Shield, GraduationCap, 
  Calendar, Clock, CheckCircle, XCircle, Eye, Settings, BarChart3,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailNotificationsPage = () => {
  const [preferences, setPreferences] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { toast } = useToast();

  const userId = 'current-user'; // Would come from auth context

  useEffect(() => {
    fetchAll();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [prefsRes, templatesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/email-notifications/preferences/${userId}`),
        fetch(`${API_URL}/api/email-notifications/templates`),
        fetch(`${API_URL}/api/email-notifications/stats?days=30`)
      ]);

      if (prefsRes.ok) setPreferences(await prefsRes.json());
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setTemplates(data.templates);
      }
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (error) {
      toast({ title: "Error", description: "Failed to load settings", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const fetchLogs = async () => {
    try {
      const response = await fetch(`${API_URL}/api/email-notifications/logs?page=${page}&page_size=20`);
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs);
        setTotalPages(data.pagination.total_pages);
      }
    } catch (error) {
      console.error('Failed to load logs');
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/email-notifications/preferences/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(preferences.preferences)
      });

      if (response.ok) {
        toast({ title: "Success", description: "Preferences saved" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save preferences", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const updatePreference = (key, value) => {
    setPreferences({
      ...preferences,
      preferences: {
        ...preferences.preferences,
        [key]: value
      }
    });
  };

  const sendTestEmail = async (type) => {
    try {
      const response = await fetch(`${API_URL}/api/email-notifications/send/weekly-digest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipients: ['test@company.com'] })
      });

      if (response.ok) {
        toast({ title: "Test Email Sent", description: "Check your inbox" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to send test email", variant: "destructive" });
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'sent' || status === 'sent_demo') {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    if (status === 'failed') {
      return <XCircle className="w-4 h-4 text-red-500" />;
    }
    return <Clock className="w-4 h-4 text-yellow-500" />;
  };

  const getTypeIcon = (type) => {
    const icons = {
      incident_alert: AlertTriangle,
      compliance_reminder: Shield,
      training_due: GraduationCap,
      weekly_digest: Calendar,
      hazard_detected: AlertTriangle,
      approval_required: CheckCircle
    };
    const Icon = icons[type] || Mail;
    return <Icon className="w-4 h-4" />;
  };

  const COLORS = ['#22c55e', '#ef4444', '#f59e0b'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Mail className="w-7 h-7 text-blue-600" />
            Email Notifications
          </h1>
          <p className="text-gray-500 mt-1">Configure email alerts and notification preferences</p>
        </div>
        <Button onClick={savePreferences} disabled={saving} className="gap-2" data-testid="save-preferences-btn">
          {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Settings className="w-4 h-4" />}
          Save Preferences
        </Button>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="email-stats">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Send className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_sent}</p>
                  <p className="text-sm text-gray-500">Emails Sent (30d)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.delivery_rate}%</p>
                  <p className="text-sm text-gray-500">Delivery Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_type.find(t => t.type === 'incident_alert')?.count || 0}</p>
                  <p className="text-sm text-gray-500">Incident Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg">
                  <Calendar className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.by_type.find(t => t.type === 'weekly_digest')?.count || 0}</p>
                  <p className="text-sm text-gray-500">Weekly Digests</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <Tabs defaultValue="preferences" className="space-y-4">
        <TabsList>
          <TabsTrigger value="preferences">Notification Preferences</TabsTrigger>
          <TabsTrigger value="templates">Email Templates</TabsTrigger>
          <TabsTrigger value="history">Email History</TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* Preferences Tab */}
        <TabsContent value="preferences" className="space-y-4">
          {preferences && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Incident Alerts */}
              <Card data-testid="incident-alerts-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Incident Alerts
                  </CardTitle>
                  <CardDescription>Get notified when incidents are reported</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable Incident Alerts</Label>
                    <Switch
                      checked={preferences.preferences.incident_alerts}
                      onCheckedChange={(v) => updatePreference('incident_alerts', v)}
                    />
                  </div>
                  <div>
                    <Label>Alert for Severity</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {['serious', 'moderate', 'minor'].map((sev) => (
                        <Badge
                          key={sev}
                          variant={preferences.preferences.incident_alert_severity?.includes(sev) ? 'default' : 'outline'}
                          className="cursor-pointer"
                          onClick={() => {
                            const current = preferences.preferences.incident_alert_severity || [];
                            const updated = current.includes(sev)
                              ? current.filter(s => s !== sev)
                              : [...current, sev];
                            updatePreference('incident_alert_severity', updated);
                          }}
                        >
                          {sev}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Compliance Reminders */}
              <Card data-testid="compliance-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-green-600" />
                    Compliance Reminders
                  </CardTitle>
                  <CardDescription>Deadline reminders for compliance items</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable Compliance Reminders</Label>
                    <Switch
                      checked={preferences.preferences.compliance_reminders}
                      onCheckedChange={(v) => updatePreference('compliance_reminders', v)}
                    />
                  </div>
                  <div>
                    <Label>Days Before Deadline</Label>
                    <Select
                      value={String(preferences.preferences.compliance_days_before || 7)}
                      onValueChange={(v) => updatePreference('compliance_days_before', parseInt(v))}
                    >
                      <SelectTrigger className="w-32 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 days</SelectItem>
                        <SelectItem value="7">7 days</SelectItem>
                        <SelectItem value="14">14 days</SelectItem>
                        <SelectItem value="30">30 days</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </Card>

              {/* Training Reminders */}
              <Card data-testid="training-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <GraduationCap className="w-5 h-5 text-blue-600" />
                    Training Reminders
                  </CardTitle>
                  <CardDescription>Reminders for upcoming training deadlines</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable Training Reminders</Label>
                    <Switch
                      checked={preferences.preferences.training_reminders}
                      onCheckedChange={(v) => updatePreference('training_reminders', v)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Digest Settings */}
              <Card data-testid="digest-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-purple-600" />
                    Safety Digests
                  </CardTitle>
                  <CardDescription>Weekly and monthly summary emails</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Weekly Digest</Label>
                    <Switch
                      checked={preferences.preferences.weekly_digest}
                      onCheckedChange={(v) => updatePreference('weekly_digest', v)}
                    />
                  </div>
                  <div>
                    <Label>Send on Day</Label>
                    <Select
                      value={String(preferences.preferences.weekly_digest_day || 1)}
                      onValueChange={(v) => updatePreference('weekly_digest_day', parseInt(v))}
                    >
                      <SelectTrigger className="w-32 mt-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sunday</SelectItem>
                        <SelectItem value="1">Monday</SelectItem>
                        <SelectItem value="2">Tuesday</SelectItem>
                        <SelectItem value="3">Wednesday</SelectItem>
                        <SelectItem value="4">Thursday</SelectItem>
                        <SelectItem value="5">Friday</SelectItem>
                        <SelectItem value="6">Saturday</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Separator />
                  <div className="flex items-center justify-between">
                    <Label>Monthly Summary</Label>
                    <Switch
                      checked={preferences.preferences.monthly_summary}
                      onCheckedChange={(v) => updatePreference('monthly_summary', v)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Hazard Alerts */}
              <Card data-testid="hazard-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Eye className="w-5 h-5 text-yellow-600" />
                    Hazard Detection Alerts
                  </CardTitle>
                  <CardDescription>Alerts from AI hazard scanning</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable Hazard Alerts</Label>
                    <Switch
                      checked={preferences.preferences.hazard_alerts}
                      onCheckedChange={(v) => updatePreference('hazard_alerts', v)}
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Approval Notifications */}
              <Card data-testid="approval-settings">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Approval Notifications
                  </CardTitle>
                  <CardDescription>Notifications when approvals are needed</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Label>Enable Approval Notifications</Label>
                    <Switch
                      checked={preferences.preferences.approval_notifications}
                      onCheckedChange={(v) => updatePreference('approval_notifications', v)}
                    />
                  </div>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          <Card data-testid="email-templates">
            <CardHeader>
              <CardTitle>Available Email Templates</CardTitle>
              <CardDescription>Preview and test email templates</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {templates.map((template) => (
                  <div key={template.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                        {getTypeIcon(template.notification_type)}
                      </div>
                      <div>
                        <h4 className="font-medium">{template.name}</h4>
                        <p className="text-sm text-gray-500">{template.subject_preview}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" className="gap-1">
                        <Eye className="w-4 h-4" />
                        Preview
                      </Button>
                      <Button variant="outline" size="sm" className="gap-1" onClick={() => sendTestEmail(template.notification_type)}>
                        <Send className="w-4 h-4" />
                        Test
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* History Tab */}
        <TabsContent value="history" className="space-y-4">
          <Card data-testid="email-history">
            <CardHeader>
              <CardTitle>Email History</CardTitle>
              <CardDescription>Recent email notifications sent</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {logs.map((log) => (
                  <div key={log.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex items-center gap-3">
                      {getStatusIcon(log.status)}
                      <div>
                        <p className="font-medium text-sm">{log.subject}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">{log.notification_type.replace(/_/g, ' ')}</Badge>
                          <span className="text-xs text-gray-500">
                            {new Date(log.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-sm text-gray-500">
                      {log.to?.join(', ')}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-4">
          {stats && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle>Emails by Type</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.by_type}
                        dataKey="count"
                        nameKey="type"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label={({ type, count }) => `${type.replace(/_/g, ' ')}: ${count}`}
                      >
                        {stats.by_type.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'][index % 6]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Delivery Status</CardTitle>
                </CardHeader>
                <CardContent className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={stats.by_status}
                        dataKey="count"
                        nameKey="status"
                        cx="50%"
                        cy="50%"
                        outerRadius={80}
                        label
                      >
                        {stats.by_status.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailNotificationsPage;
